import React from 'react';
import { makeStyles } from 'tss-react/mui';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Moment from 'react-moment';
import Image from 'next/image';
import Link from '../Link';
import { buildImageLoaderUrl } from '../../utils/utils';

const useStyles = makeStyles()((theme) => ({
  root: {
    minWidth: 120,
    width: '240px',
    padding: 'inherit',
    boxShadow: '0px 5px 26px -10px rgba(0, 0, 0, 0.46)',
    margin: '10px auto',
    [theme.breakpoints.down('md')]: {
      width: 'min(92vw, 320px)',
      margin: '8px auto',
    },
    '&:hover': {
      cursor: 'pointer',
    },
  },
  bullet: {
    display: 'inline-block',
    margin: '0 2px',
    transform: 'scale(0.8)',
  },
  pos: {
    marginBottom: 12,
  },
  categorie: {
    backgroundColor: 'white',
    borderRadius: '0.3em',
    color: '#f0a300',
    width: 'max-content',
    padding: '0 5px 0 5px',
    display: 'block',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  image: {
    backgroundPosition: 'center center',
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
    textAlign: 'inherit',
    height: '10em',
    [theme.breakpoints.down('md')]: {
      height: '8.5em',
    },
  },
  title: {
    textAlign: 'left',
    color: '#2C367E',
    width: '100%',
    fontSize: '1.2rem',
    lineHeight: 1.3,
    [theme.breakpoints.down('md')]: {
      fontSize: '0.95rem',
    },
  },
  content: {
    padding: '10px',
    [theme.breakpoints.down('md')]: {
      padding: '8px',
    },
  },
  date: {
    textAlign: 'right',
    color: '#2C367E',
    [theme.breakpoints.down('md')]: {
      fontSize: '0.9rem',
    },
  },
  titleDiv: {
    display: 'flex',
    alignItems: 'center',
  },
}));

interface Category {
  label?: string;
}

interface Picture {
  logo?: boolean;
  originalPicturePath?: string | null;
}

interface Event {
  url: string;
  label: string;
  startedAt: number;
  endedAt: number;
  duration?: string;
  categories?: Category[];
  pictures: Picture[];
}

interface CardSliderEventProps {
  event: Event;
}

export default function CardSliderEvent({ event }: CardSliderEventProps) {
  const startDateFormat = '[De ]HH[h]mm';
  const endDateFormat = '[ à ]HH[h]mm';

  const { classes } = useStyles();
  const myLoader = ({ src, width, quality }: { src: string; width: number; quality?: number }) => {
    return buildImageLoaderUrl(src, width, quality);
  };

  const mainPicture = Array.isArray(event?.pictures) ? event.pictures.filter((a) => (a.logo ? -1 : 1))[0] : undefined;

  return (
    <Link href={`/evenement/${event.url}`}>
      <Card className={classes.root}>
        <CardContent>
          <div className={classes.image}>
            <div className={classes.categorie}>
              <Typography className={classes.categorie} gutterBottom>
                {event.categories && event.categories.length > 0 && event.categories[0].label}
              </Typography>
            </div>
            {mainPicture && mainPicture.originalPicturePath != null && (
              <div style={{ position: 'relative', height: '130px' }}>
                <Image
                  loader={myLoader}
                  fill
                  style={{ objectFit: 'contain' }}
                  src={mainPicture.originalPicturePath}
                  alt={event.label}
                />
              </div>
            )}
          </div>
          <div className={classes.content}>
            <Moment locale="fr" format="DD MMMM YYYY" className={classes.date} unix>
              {event.startedAt / 1000}
            </Moment>
            <br />

            {!event.duration && (
              <>
                <Moment format={startDateFormat} unix>
                  {event.startedAt / 1000}
                </Moment>
                <Moment format={endDateFormat} unix>
                  {event.endedAt / 1000}
                </Moment>
                <br />
              </>
            )}
            {event.duration && (
              <>
                {event.duration}
                <br />
              </>
            )}
            <div className={classes.titleDiv}>
              <Typography className={classes.title}>{event && event.label}</Typography>
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
